// =============================================================================
// TopUpTopInfoStrip.jsx — header cards for the Top-up approval view
// =============================================================================
// Mirrors the responder request header (StaffTopInfoStrip) for a Stock Top-up:
//   General Information · DFV Service Notes (reused ShelterInfoBox) · Summary
//   (per-section Requested/Approved item counts + a People button).
//
// The delivery block (address + verify/ban/unregistered warnings + Verify + Edit)
// is a DELIBERATE DUPLICATE of the one in StaffTopInfoStrip, kept separate so the
// live responder view can never be affected by top-up changes (John, 4 Sep 2026).
//
// ⚠️ CROSS-REFERENCE: the delivery-warning block below is a copy of the one in
// src/components/request/StaffTopInfoStrip.jsx. If you change the verify / ban /
// unregistered warning logic in EITHER file, check the OTHER.
// =============================================================================
import { useState } from "react";
import { C } from "../../utils/constants";
import { formatPhone } from "../../utils/sanitize";
import { ShelterInfoBox } from "./RequestDetailPanels.jsx";
import { RequestPeopleModal } from "./RequestPeopleModal.jsx";
import { VerifyAddressModal } from "./VerifyAddressModal.jsx";

const BORDER = "#CFE0DE";

const labelCell = {
  fontSize: 11,
  fontWeight: 700,
  color: "#607D79",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  width: 120,
  flexShrink: 0,
};

// Normalise an address for the "unregistered" comparison (same rules as StaffTopInfoStrip).
function normAddr(s) {
  return (s || "").toLowerCase().replace(/\s+/g, " ").replace(/,\s*/g, ", ").trim();
}

// The blue-header card shell shared by General Information + Summary.
function InfoCard({ title, right, children }) {
  return (
    <div
      style={{
        marginBottom: 0,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: C.blue,
          color: "white",
          padding: "0 18px",
          height: 48,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          fontWeight: 700,
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        <span>{title}</span>
        {right || null}
      </div>
      <div style={{ padding: "14px 16px", flex: 1, display: "flex", flexDirection: "column" }}>
        {children}
      </div>
    </div>
  );
}

// One label / value row (hidden when the value is empty).
function Row({ label, value }) {
  if (!value) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 0,
        marginBottom: 6,
        paddingBottom: 6,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <div style={labelCell}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#1A2E2B", flex: 1 }}>{value}</div>
    </div>
  );
}

export function TopUpTopInfoStrip({
  req,
  sections,
  locked,
  shelterRegAddrs,
  shelterAddrsLoaded,
  setEditDelivery,
  setShowAddressModal,
  onVerifyAddress,
  user,
}) {
  const [showPeople, setShowPeople] = useState(false);
  const [showVerify, setShowVerify] = useState(false);

  const address = req?.delivery_address_resolved || "";
  const canVerifyAddress =
    req?.delivery_address_verified === false && !!req?.delivery_address_id && !!onVerifyAddress;

  const nums =
    req?.mobile || req?.phone ? (
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 14 }}>
        {req?.mobile ? <span>{formatPhone(req.mobile)}</span> : null}
        {req?.phone ? <span>{formatPhone(req.phone)}</span> : null}
      </div>
    ) : null;

  // "Unregistered address" — same rule as the responder view: warn when the address
  // doesn't match any registered address and isn't already pending verification.
  const addrNorm = normAddr(address);
  const matchesReg =
    shelterRegAddrs.length > 0 &&
    shelterRegAddrs.some(function (r) {
      const reg = normAddr(r);
      return reg === addrNorm || addrNorm.indexOf(reg) >= 0 || reg.indexOf(addrNorm) >= 0;
    });
  const isUnreg =
    req?.delivery_address_verified !== false &&
    !matchesReg &&
    (req?.non_registered_address || (shelterAddrsLoaded && shelterRegAddrs.length > 0));

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 12,
        marginBottom: 14,
        alignItems: "stretch",
      }}
    >
      {/* General information */}
      <InfoCard title="General Information">
        <Row label="DFV Service Ref" value={req?.shelter_ref} />
        <Row label="DFV Service" value={req?.shelter_name} />
        <Row label="Service Code" value={req?.shelter_code} />
        <Row label="Contact Name" value={req?.contact_name} />
        <Row label="Contact Email" value={req?.shelter_email} />
        <Row label="Mobile / Phone" value={nums} />

        {/* Delivery: label + address on one row; warnings + Edit full-width below. */}
        <div style={{ marginTop: 4 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 0, marginBottom: 6 }}>
            <div style={labelCell}>Delivery</div>
            <div style={{ fontSize: 13, color: "#1A2E2B", lineHeight: 1.5, flex: 1 }}>
              {address || "—"}
            </div>
          </div>
          <div>
            {req?.delivery_address_banned === true && (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#fff",
                  background: "#B71C1C",
                  border: "1px solid #7f1010",
                  borderRadius: 5,
                  padding: "5px 9px",
                  marginBottom: 6,
                }}
              >
                ⛔ Delivery address BANNED — do not use. Change the address (Edit Address) or ask a
                supervisor to remove the ban.
              </div>
            )}
            {req?.delivery_address_verified === false && req?.delivery_address_banned !== true && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                {canVerifyAddress && (
                  <button
                    type="button"
                    onClick={function () {
                      setShowVerify(true);
                    }}
                    title="Verify this delivery address"
                    style={{
                      background: "#F57C00",
                      color: "white",
                      border: "none",
                      borderRadius: 6,
                      padding: "5px 12px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      flexShrink: 0,
                    }}
                  >
                    ✓ Verify address
                  </button>
                )}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#7c4a00",
                    background: "#FFF3E0",
                    border: "1px solid #FFB300",
                    borderRadius: 5,
                    padding: "4px 8px",
                  }}
                >
                  ⚠ Delivery address pending Zephyr verification
                </div>
              </div>
            )}
            {isUnreg && (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "#C62828",
                  marginBottom: 6,
                  background: "#FFEBEE",
                  border: "1px solid #EF9A9A",
                  borderRadius: 5,
                  padding: "4px 8px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                ⚠ Unregistered address — verify with shelter
              </div>
            )}
            <button
              type="button"
              onClick={function () {
                setEditDelivery(address || "");
                setShowAddressModal(true);
              }}
              disabled={locked}
              title={
                locked ? "Locked — this top-up has been Confirmed & Sent." : "Edit delivery address"
              }
              style={{
                background: locked ? "#B0BEC5" : "#00897B",
                color: "white",
                border: "none",
                borderRadius: 6,
                padding: "5px 12px",
                fontSize: 12,
                fontWeight: 600,
                cursor: locked ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              ✎ Edit Address
            </button>
          </div>
        </div>
      </InfoCard>

      {/* DFV Service Notes (Responders Only) — reused verbatim from the request view. */}
      <ShelterInfoBox
        shelterCode={req?.shelter_code}
        shelterName={req?.shelter_name}
        user={user}
        lockEdits={locked}
      />

      {/* Summary — per-section Requested / Approved item counts + People button. */}
      <InfoCard
        title="Summary"
        right={
          <button
            type="button"
            onClick={function () {
              setShowPeople(true);
            }}
            title="See everyone who has worked on this top-up"
            style={{
              background: "#E3F2FD",
              color: "#37474F",
              border: `1px solid ${BORDER}`,
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            People on this top-up
          </button>
        }
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 11,
            fontWeight: 700,
            color: "#607D79",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            paddingBottom: 4,
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <span>Section</span>
          <span>
            <span style={{ display: "inline-block", width: 70, textAlign: "right" }}>
              Requested
            </span>
            <span style={{ display: "inline-block", width: 70, textAlign: "right" }}>Approved</span>
          </span>
        </div>
        {(sections || []).map(function (s) {
          return (
            <div
              key={s.key}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 13,
                padding: "4px 0",
                borderBottom: `1px solid ${BORDER}`,
              }}
            >
              <span>{s.label}</span>
              <span style={{ fontWeight: 600, color: "#1A2E2B" }}>
                <span style={{ display: "inline-block", width: 70, textAlign: "right" }}>
                  {s.requested}
                </span>
                <span style={{ display: "inline-block", width: 70, textAlign: "right" }}>
                  {s.approved}
                </span>
              </span>
            </div>
          );
        })}
        {(sections || []).length === 0 && (
          <div style={{ fontSize: 13, color: "#8AA5A1", padding: "6px 0" }}>No items.</div>
        )}
      </InfoCard>

      {showPeople && (
        <RequestPeopleModal
          requestId={req?.id}
          refNo={req?.ref_no}
          onClose={function () {
            setShowPeople(false);
          }}
        />
      )}
      {showVerify && (
        <VerifyAddressModal
          onCancel={function () {
            setShowVerify(false);
          }}
          onConfirm={async function () {
            await onVerifyAddress();
            setShowVerify(false);
          }}
        />
      )}
    </div>
  );
}
